import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useAuth';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || '';

/**
 * Singleton socket — multiple components calling `useRealtime()` share one
 * connection. The socket connects after `useCurrentUser` resolves so the
 * `patronez_session` cookie is in flight, then auto-reconnects on disconnect.
 *
 * On every `notification.created` push, react-query's notifications cache
 * is invalidated so the NotificationBell badge updates immediately without
 * a manual refetch.
 */
let sharedSocket: Socket | null = null;

function getSocket(): Socket {
  if (sharedSocket) return sharedSocket;
  sharedSocket = io(`${API_URL}/realtime`, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1500,
    reconnectionDelayMax: 10_000,
    autoConnect: true,
  });
  return sharedSocket;
}

export interface RealtimeNotificationPayload {
  id: string;
  userId: string;
  workspaceId: string | null;
  kind: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
}

export function useRealtime() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const handlersInstalled = useRef(false);

  useEffect(() => {
    if (!me.data) return;
    const socket = getSocket();

    function onConnect() {
      setConnected(true);
    }
    function onDisconnect() {
      setConnected(false);
    }
    function onNotification(payload: RealtimeNotificationPayload) {
      // The bell pulls a query for unread; invalidate so it refetches.
      qc.invalidateQueries({ queryKey: ['notifications'] });
      // Browser title-blink for users who don't have the tab focused.
      if (document.hidden) {
        const original = document.title;
        document.title = `● ${original}`;
        const restore = () => {
          document.title = original;
          window.removeEventListener('focus', restore);
        };
        window.addEventListener('focus', restore);
      }
    }

    if (!handlersInstalled.current) {
      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('notification.created', onNotification);
      handlersInstalled.current = true;
    }

    return () => {
      // Only the last consumer should remove handlers; but since this hook
      // is currently used only by the AppShell (mounts once for the whole
      // session), leaving handlers attached is fine. Disconnection happens
      // on logout via the explicit `disconnectRealtime()` below.
    };
  }, [me.data, qc]);

  return { connected };
}

/** Call on logout — closes the socket so the next login establishes
 *  a fresh authenticated connection. */
export function disconnectRealtime(): void {
  if (sharedSocket) {
    sharedSocket.disconnect();
    sharedSocket = null;
  }
}
